import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertCircle, AlertTriangle, Loader2 } from 'lucide-react';
import { useArchiveSite, useSiteAssets } from '@/hooks/siteHooks';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import { ProjectFullDto } from '../siteApi';
import { deleteDevice } from '@/lib/api/devices/devices';
import { DatalayerAssetExtendedDto } from '@/types/datalayer-asset';

interface SiteDangerZoneProps {
  customerId: string;
  siteId: string;
  site: ProjectFullDto;
  archived: boolean;
  asDropdownItem?: boolean;
}

function resolveNvrId(device: DatalayerAssetExtendedDto): string | undefined {
  const speakerNvrId = device.speakers?.[0]?.nvrId;
  const relayNvrId = device.relays?.[0]?.nvrId;
  const vpcNvrId = device.videoProcessorConfigs.find((x) => x?.nvrId)?.nvrId;
  return device.nvrId || speakerNvrId || relayNvrId || vpcNvrId;
}

export function SiteDangerZone({
  customerId,
  siteId,
  site,
  archived,
  asDropdownItem = false,
}: SiteDangerZoneProps) {
  const [open, setOpen] = useState(false);
  const [keepDevices, setKeepDevices] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const { mutate: archiveSite, isPending } = useArchiveSite();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { data: siteAssets } = useSiteAssets({ siteId, companyId: customerId });
  const activeDevices = siteAssets?.filter((d) => d.deletedAt == null) ?? [];
  const deviceCount = activeDevices.length;

  const isBusy = isPending || isDeleting;

  const handleArchive = () => {
    archiveSite(
      { companyId: customerId, siteId, site, archive: !archived },
      {
        onSuccess: async () => {
          if (archived) {
            setOpen(false);
            return;
          }

          const deletableDevices = activeDevices
            .map((d) => ({ device: d, nvrId: resolveNvrId(d) }))
            .filter((d): d is { device: DatalayerAssetExtendedDto; nvrId: string } => d.nvrId != null);

          if (keepDevices || deletableDevices.length === 0) {
            navigate(-1);
            setOpen(false);
            return;
          }

          setIsDeleting(true);

          const errors: string[] = [];
          for (const { device, nvrId } of deletableDevices) {
            try {
              await deleteDevice({
                deviceId: nvrId,
                nvr: {
                  externalId: device.externalId,
                  name: device.name,
                },
                monitoredDevices: [],
                integrationId: device.integrationId,
                customerId: device.companyId,
              });
            } catch {
              errors.push(device.name || nvrId);
            }
          }

          queryClient.invalidateQueries({ queryKey: ['siteAssets'] });
          queryClient.invalidateQueries({ queryKey: ['devices.search'] });
          queryClient.invalidateQueries({ queryKey: ['siteDevices'] });

          if (errors.length > 0) {
            toast({
              title: `${deletableDevices.length - errors.length} of ${deletableDevices.length} devices deleted`,
              description: `Failed: ${errors.join(', ')}`,
              variant: 'destructive',
            });
          } else {
            toast({
              title: 'All devices deleted',
              description: `${deletableDevices.length} device(s) removed successfully`,
            });
          }

          setIsDeleting(false);
          navigate(-1);
          setOpen(false);
        },
      }
    );
  };

  const handleClick = () => setOpen(true);

  const handleOpenChange = (isOpen: boolean) => {
    if (isDeleting) return;
    setOpen(isOpen);
    if (!isOpen) {
      setKeepDevices(false);
    }
  };

  const label = archived ? 'Restore Site' : 'Archive Site';

  return (
    <>
      {asDropdownItem ? (
        <DropdownMenuItem
          onSelect={(e) => {
            e.preventDefault();
            handleClick();
          }}
          className={`flex items-center gap-2 ${archived ? '' : 'text-red-500'}`}
        >
          <AlertCircle className="h-4 w-4" />
          {label}
        </DropdownMenuItem>
      ) : (
        <Button variant={archived ? 'outline' : 'destructive'} size="sm" onClick={handleClick}>
          {label}
        </Button>
      )}

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="bg-[#111] text-white sm:max-w-[520px]">
          <DialogHeader>
            <DialogTitle>Are you absolutely sure?</DialogTitle>
            <DialogDescription className="text-white/70">
              {archived
                ? `This will restore the site "${site.name}" and make it visible in the main site list.`
                : `Archiving the site "${site.name}" hides it from active lists but keeps its data. You can restore it later from the sites page.`}
            </DialogDescription>
          </DialogHeader>

          {!archived && deviceCount > 0 && (
            <div className="rounded-lg border border-white/10 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 shrink-0 text-yellow-500" />
                <p className="text-yellow-500 font-medium">
                  {deviceCount} device{deviceCount !== 1 ? 's' : ''} will be deleted
                </p>
              </div>
              <p className="text-sm text-white/70">
                All devices on this site will be permanently deleted after archiving.
                You can move devices to another site before proceeding.
              </p>
              <div className="flex items-start gap-2 pt-1">
                <Checkbox
                  id="keep-devices"
                  checked={keepDevices}
                  onCheckedChange={(checked) => setKeepDevices(checked === true)}
                  disabled={isBusy}
                />
                <label htmlFor="keep-devices" className="text-sm leading-tight cursor-pointer text-white/70">
                  Keep devices (archive site only, do not delete devices)
                </label>
              </div>
            </div>
          )}

          <DialogFooter className="flex-col-reverse sm:flex-col-reverse gap-2">
            {archived ? (
              <DialogClose asChild>
                <Button
                  onClick={handleArchive}
                  variant="default"
                  disabled={isPending}
                  className="w-full"
                >
                  {isPending ? 'Restoring...' : label}
                </Button>
              </DialogClose>
            ) : (
              <Button
                onClick={handleArchive}
                variant="destructive"
                disabled={isBusy}
                className="w-full"
              >
                {isBusy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {isDeleting ? 'Deleting devices...' : isPending ? 'Archiving...' : label}
              </Button>
            )}
            <Button variant="ghost" onClick={() => handleOpenChange(false)} disabled={isDeleting} className="w-full">
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
